import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import roomApi, {
  RoomDetail,
  RoomFilters,
  RoomMatch,
  RoomMovie,
} from "../../api/room";
import { ROUTES } from "../../constants/routes";
import { genresData } from "../../constants/structure";
import styles from "./Room.module.scss";

import RoomEntry from "./components/RoomEntry";
import RoomLobby from "./components/RoomLobby";
import RoomHeader from "./components/RoomHeader";
import RoomVideoPlayer from "./components/RoomVideoPlayer";
import RoomMovieCards from "./components/RoomMovieCards";
import RoomMovieInfo from "./components/RoomMovieInfo";
import RoomMatchesSidebar from "./components/RoomMatchesSidebar";
import RoomInviteModal from "./components/RoomInviteModal";
import RoomGenreFilter from "./components/RoomGenreFilter";
import type { Swiper as SwiperType } from "swiper";
import { useDispatch } from "react-redux";
import useFavouriteClick from "../../hooks/useFavouriteClick";
import useLikeClick from "../../hooks/useLikeClick";
import { setLoginModal } from "../../store/slices/modals.slice";
import LinkGuide from "../../components/shared/LinkGuide/LinkGuide";
import OverlayRatingIcon from "../../components/icons/searchIcons/OverlayRatingIcon";
import OverlayTomatoRatingIcon from "../../components/icons/searchIcons/OverlayTomatoRatingIcon";
import LikeButton from "../../components/shared/LikeButton";
import SavedIcon from "../../components/icons/searchIcons/savedIcon";
import { setVoteCount } from "../../store/slices/lists.slice";
import useInterfaceContext from "../../components/providers/interface";
import {movieTitleHandler} from "../../utils/helpers";

const STATUS_POLL_MS = 3000;

type Phase = "entry" | "lobby" | "browse" | "results";

const RoomPage: React.FC = () => {
  const { code: urlCode } = useParams<{ code?: string }>();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const swiperRef = useRef<SwiperType | null>(null);


  const [phase, setPhase] = useState<Phase>(urlCode ? "lobby" : "entry");
  const [room, setRoom] = useState<RoomDetail | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [movies, setMovies] = useState<RoomMovie[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [matches, setMatches] = useState<RoomMatch[]>([]);
  const [swiping, setSwiping] = useState(false);
  const [swipedIds, setSwipedIds] = useState<Set<number>>(new Set());
  const [muted, setMuted] = useState(true);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [genreFilterOpen, setGenreFilterOpen] = useState(false);
  const [matchToast, setMatchToast] = useState(false);
  const matchToastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [selectedGenreIds, setSelectedGenreIds] = useState<number[]>([]);
  const { isMobile } = useInterfaceContext();

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const dispatch = useDispatch();
  const { favoritesList, favouritesHandler } = useFavouriteClick();
  const { likesList, likeHandler } = useLikeClick();
  const openLoginModal = () => dispatch(setLoginModal(true));

  const clearError = () => setError("");

  const showMatchToast = () => {
    setMatchToast(true);
    if (matchToastTimer.current) clearTimeout(matchToastTimer.current);
    matchToastTimer.current = setTimeout(() => setMatchToast(false), 2500);
  };

  const getUserId = (): number | null => {
    const token = localStorage.getItem("accessToken");
    if (!token) return null;
    try {
      return JSON.parse(atob(token.split(".")[1])).user_id;
    } catch {
      return null;
    }
  };
  const userId = getUserId();
  const isCreator = room ? room.creator_id === userId : false;

  useEffect(() => {
    if (urlCode) loadRoom(urlCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlCode]);

  useEffect(() => {
  }, [currentIndex]);

  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  const applyRoomState = (data: RoomDetail, code: string) => {
    setRoom(data);
    if (data.filters?.genre_ids) {
      setSelectedGenreIds(data.filters.genre_ids);
    }
    if (data.status === "waiting") {
      setPhase("lobby");
    } else if (data.status === "active") {
      setPhase("browse");
      fetchMovies(code);
      fetchMatches(code);
    } else {
      setPhase("results");
      fetchMatches(code);
    }
  };

  const loadRoom = async (code: string) => {
    setLoading(true);
    clearError();
    try {
      const { data } = await roomApi.getDetail(code);
      applyRoomState(data, code);
    } catch (err: any) {
      if (err.response?.status === 403) {
        try {
          const { data } = await roomApi.join(code);
          applyRoomState(data, code);
          return;
        } catch (joinErr: any) {
          setError(joinErr.response?.data?.detail || "Failed to join room");
        }
      } else {
        setError(err.response?.data?.detail || "Failed to load room");
      }
      setPhase("entry");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (filters: RoomFilters) => {
    setLoading(true);
    clearError();
    try {
      const { data } = await roomApi.create({ ...filters, language: i18n.language });
      setRoom(data);
      if (filters.genre_ids) setSelectedGenreIds(filters.genre_ids);
      setPhase("lobby");
      navigate(`${ROUTES.ROOM}/${data.code}`, { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to create room");
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async (code: string) => {
    setLoading(true);
    clearError();
    try {
      const { data } = await roomApi.join(code);
      setRoom(data);
      setPhase("lobby");
      navigate(`${ROUTES.ROOM}/${data.code}`, { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to join room");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (phase === "lobby" && room) {
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await roomApi.getStatus(room.code);
          setRoom((prev) =>
            prev
              ? {
                ...prev,
                status: data.status as RoomDetail["status"],
                participants: data.participants,
                matches: data.matches,
              }
              : prev
          );
          if (data.status === "active") {
            setPhase("browse");
            fetchMovies(room.code);
          } else if (["completed", "expired", "cancelled"].includes(data.status)) {
            setPhase("results");
          }
        } catch {
          // ignore polling errors
        }
      }, STATUS_POLL_MS);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, room?.code]);

  const handleStart = async () => {
    if (!room) return;
    setLoading(true);
    clearError();
    try {
      const { data } = await roomApi.start(room.code);
      setRoom(data);
      setPhase("browse");
      fetchMovies(room.code);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Cannot start yet");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = async () => {
    if (!room) return;
    try {
      await roomApi.close(room.code);
      const { data } = await roomApi.getDetail(room.code);
      setRoom(data);
      setPhase("results");
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to close room");
    }
  };

  const fetchMovies = async (code: string) => {
    try {
      const { data } = await roomApi.getMovies(code, 20);
      setMovies(data.results.filter((m: RoomMovie) => m.trailer_key !== null));
      setHasMore(data.has_more);
      setCurrentIndex(0);
    } catch {
      // ignore
    }
  };

  const fetchMatches = async (code: string) => {
    try {
      const { data } = await roomApi.getMatches(code);
      setMatches([...data].sort((a, b) => a.id - b.id));
    } catch {
      // ignore
    }
  };

  const fetchMoreMovies = useCallback(async () => {
    if (!room || !hasMore) return;
    try {
      const offset = movies.length;
      const { data } = await roomApi.getMoviesAtOffset(room.code, offset, 20);
      setMovies((prev) => {
        const existingIds = new Set(prev.map((m) => m.id));
        const newMovies = data.results.filter((m: RoomMovie) => m.trailer_key !== null && !existingIds.has(m.id));
        return [...prev, ...newMovies];
      });
      setHasMore(data.has_more);
    } catch {
      // ignore
    }
  }, [room, hasMore, movies.length]);

  const handleBrokenTrailer = useCallback(() => {
    const movie = movies[currentIndex];
    if (!movie) return;
    const brokenId = movie.id;
    const brokenIndex = currentIndex;

    setMovies((prev) => prev.filter((m) => m.id !== brokenId));

    setSwipedIds((prev) => {
      if (!prev.has(brokenId)) return prev;
      const next = new Set(prev);
      next.delete(brokenId);
      return next;
    });

    setSwipeHistory((prev) =>
      prev
        .filter((entry) => entry.movieId !== brokenId)
        .map((entry) =>
          entry.index > brokenIndex ? { ...entry, index: entry.index - 1 } : entry
        )
    );
  }, [movies, currentIndex]);

  const handleNavigateUp = useCallback(() => {
    if (currentIndex > 0) {
      swiperRef.current?.slidePrev();
    }
  }, [currentIndex]);

  const handleNavigateDown = useCallback(() => {
    if (currentIndex < movies.length - 1) {
      swiperRef.current?.slideNext();
      if (currentIndex >= movies.length - 5 && hasMore) {
        fetchMoreMovies();
      }
    }
  }, [currentIndex, movies.length, hasMore, fetchMoreMovies]);

  const [swipeHistory, setSwipeHistory] = useState<{ movieId: number; action: "like" | "dislike"; index: number }[]>([]);

  const handleLike = useCallback(async () => {
    const movie = movies[currentIndex];
    if (!room || swiping || !movie || swipedIds.has(movie.id)) return;

    setSwiping(true);

    try {
      const { data } = await roomApi.swipe(room.code, movie.id, "like");

      setSwipedIds((prev) => new Set(prev).add(movie.id));
      setSwipeHistory((prev) => [...prev, { movieId: movie.id, action: "like", index: currentIndex }]);

      setMovies((prev) =>
        prev.map((m) =>
          m.id === movie.id ? { ...m, likes_count: m.likes_count + 1 } : m
        )
      );

      if (data.match) {
        fetchMatches(room.code);
        showMatchToast();
        if (!isMobile && matches.length === 0) setSidebarOpen(true);
      }

      if (currentIndex < movies.length - 1) {
        if (isMobile) {
          setCurrentIndex((i) => i + 1);
        } else {
          swiperRef.current?.slideNext();
        }

        if (currentIndex >= movies.length - 5 && hasMore) {
          fetchMoreMovies();
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSwiping(false);
    }
  }, [
    room,
    swiping,
    movies,
    currentIndex,
    swipedIds,
    matches.length,
    hasMore,
    fetchMoreMovies,
    isMobile,
  ]);

  const handleDislike = useCallback(async () => {
    const movie = movies[currentIndex];
    if (!room || swiping || !movie || swipedIds.has(movie.id)) return;

    setSwiping(true);

    try {
      await roomApi.swipe(room.code, movie.id, "dislike");

      setSwipedIds((prev) => new Set(prev).add(movie.id));
      setSwipeHistory((prev) => [...prev, { movieId: movie.id, action: "dislike", index: currentIndex }]);

      if (currentIndex < movies.length - 1) {
        if (isMobile) {
          setCurrentIndex((i) => i + 1);
        } else {
          swiperRef.current?.slideNext();
        }

        if (currentIndex >= movies.length - 5 && hasMore) {
          fetchMoreMovies();
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSwiping(false);
    }
  }, [
    room,
    swiping,
    movies,
    currentIndex,
    swipedIds,
    hasMore,
    fetchMoreMovies,
    isMobile,
  ]);

  const handleRevert = useCallback(() => {
    if (swiping || swipeHistory.length === 0) return;

    const last = swipeHistory[swipeHistory.length - 1];

    setSwipedIds((prev) => {
      const next = new Set(prev);
      next.delete(last.movieId);
      return next;
    });

    if (last.action === "like") {
      setMovies((prev) =>
        prev.map((m) =>
          m.id === last.movieId
            ? { ...m, likes_count: Math.max(0, m.likes_count - 1) }
            : m
        )
      );
    }

    if (isMobile) {
      setCurrentIndex(last.index);
    } else {
      swiperRef.current?.slideTo(last.index);
    }

    setSwipeHistory((prev) => prev.slice(0, -1));
  }, [swiping, swipeHistory, isMobile]);

  useEffect(() => {
    if (phase !== "browse") return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowUp") handleNavigateUp();
      if (e.key === "ArrowDown") handleNavigateDown();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [phase, handleNavigateUp, handleNavigateDown]);

  useEffect(() => {
    if (phase === "browse" && room) {
      const interval = setInterval(async () => {
        try {
          const { data } = await roomApi.getStatus(room.code);
          setRoom((prev) =>
            prev
              ? {
                ...prev,
                status: data.status as RoomDetail["status"],
                participants: data.participants,
                matches: data.matches,
              }
              : prev
          );
          if (["completed", "expired", "cancelled"].includes(data.status)) {
            setPhase("results");
          }
          setMatches((prev) => {
            if (data.matches.length > prev.length) {
              showMatchToast();
              if (prev.length === 0) setSidebarOpen(true);
              if (!isMobile && prev.length === 0) setSidebarOpen(true);
              return [...data.matches].sort((a, b) => a.id - b.id);
            }
            return prev;
          });
        } catch {
          // ignore
        }
      }, STATUS_POLL_MS);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, room?.code, isMobile]);

  const handleSelectMatch = async (matchId: number) => {
    if (!room) return;
    try {
      await roomApi.selectMatch(room.code, matchId);
      const { data } = await roomApi.getDetail(room.code);
      setRoom(data);
      setPhase("results");
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to select match");
    }
  };

  const handleNewRoom = () => {
    setRoom(null);
    setPhase("entry");
    setMovies([]);
    setMatches([]);
    setCurrentIndex(0);
    setSwipedIds(new Set());
    setSidebarOpen(false);
    clearError();
    navigate(ROUTES.ROOM, { replace: true });
  };

  const toggleGenre = (id: number) => {
    setSelectedGenreIds((prev) =>
      prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]
    );
  };

  const getActiveGenreNames = (): string[] => {
    return selectedGenreIds
      .map((id) => genresData.find((g) => g.id === id)?.name)
      .filter(Boolean)
      .map((name) => t(name as string)) as string[];
  };

  const currentMovie = movies[currentIndex] || null;
  const selectedMatch = room?.matches?.find((m) => m.is_selected);
  const isCurrentMovieSwiped = currentMovie
    ? swipedIds.has(currentMovie.id)
    : false;

  const videoWrapperRef = useRef<HTMLDivElement | null>(null);
  const [videoHeight, setVideoHeight] = useState<number | null>(null);

  useEffect(() => {
    const el = videoWrapperRef.current;
    if (!el) return;

    const ro = new ResizeObserver(([entry]) => {
      setVideoHeight(entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [phase, currentMovie]);

  const selectedMatchLiked = selectedMatch
    ? (likesList?.data?.data || []).includes(selectedMatch.movie_id)
    : false;
  const selectedMatchFavorited = selectedMatch
    ? (favoritesList?.data?.results || []).includes(selectedMatch.movie_id)
    : false;

  const updateVoteCount = (id: any, key: any, type: any) => {
    dispatch(setVoteCount({ id, key, type }));
  };

  const formatLikesCount = (count: number) => {
    if (count < 1000) return `${count}`;
    const compactCount = Math.floor(count / 100) / 10;
    return `${Number.isInteger(compactCount) ? compactCount.toFixed(0) : compactCount.toFixed(1)}k`;
  };

  const getMovieMeta = (detail: RoomMovie | null | undefined) => {
    const year = detail?.release_date?.slice(0, 4) || null;
    const genreList = detail?.genres
      ? JSON.parse(detail.genres)
        .slice(0, 2)
        .map((id: number) => genresData.find((genre) => genre.id === id)?.name)
        .filter(Boolean)
        .map((name: string) => t(name))
      : [];
    return { year, genreList };
  };

  const selectedMovieDetail = selectedMatch?.movie_detail ?? null;
  const selectedImdbRating =
    selectedMovieDetail?.imdb_rating ?? selectedMovieDetail?.vote_average ?? null;
  const selectedTomatoRating = selectedMovieDetail?.rotten_tomatoes_rating ?? null;
  const selectedLikesCount = selectedMovieDetail?.likes_count ?? 0;
  const selectedMeta = getMovieMeta(selectedMovieDetail);

  const handleGoHome = () => {
    navigate(ROUTES.ROOM);
  };

  const updateMovieCountLocal = (id: number, key: "likes_count" | "favorites_count" | "shares_count", type: "increment" | "decrement") => {
    setMovies((prev) =>
      prev.map((m) =>
        m.id === id
          ? { ...m, [key]: Math.max(0, (m[key] as number) + (type === "increment" ? 1 : -1)) }
          : m
      )
    );
  };


  return (
    <>
      <Header
        className={styles.headerWrapper}
        isResultsPhase={phase === "results"}
        isBrowsePhase={phase === "browse"}
        source={ROUTES.ROOM}
      />
      <div  className={`${styles.page} ${ phase === "browse" ? styles.pageBrowse : "" } ${ phase === "results" ? styles.pageResult : "" }`}>
        {phase === "entry" && (
          <RoomEntry
            onCreateRoom={handleCreate}
            onJoinRoom={handleJoin}
            loading={loading}
            error={error}
          />
        )}

        {phase === "lobby" && room && (
          <RoomLobby
            room={room}
            isCreator={isCreator}
            onStart={handleStart}
            onClose={handleClose}
            loading={loading}
            error={error}
          />
        )}

        {phase === "browse" && room && (
          <div className={styles.browseLayout}>
            <RoomMatchesSidebar
              matches={matches}
              isOpen={sidebarOpen}
              isCreator={isCreator}
              onClose={() => {
                if (isMobile) setSidebarOpen(false);
              }}
              onSelectMatch={handleSelectMatch}
            />

            {!(isMobile && sidebarOpen) && (
              <div className={styles.mainContent}>
                <RoomHeader
                  activeGenreNames={getActiveGenreNames()}
                  filterCount={selectedGenreIds.length}
                  participants={room.participants}
                  matchCount={matches.length}
                  onFilterClick={() => setGenreFilterOpen(true)}
                  onMatchesClick={() => {
                    if (isMobile) setSidebarOpen(!sidebarOpen);
                  }}
                  onInviteClick={() => setInviteModalOpen(true)}
                  isCreator={isCreator}
                  onEndSession={handleClose}
                />

                {currentMovie ? (
                  <>
                    <div className={styles.playerSection}>
                      <div className={styles.playerRow}>
                        <div
                          className={styles.videoWrapper}
                          ref={videoWrapperRef}
                        >
                          <RoomVideoPlayer
                            trailerKey={currentMovie.trailer_key}
                            backdropPath={currentMovie.backdrop_path}
                            muted={muted}
                            onToggleMuted={() => setMuted(!muted)}
                            onTrailerError={handleBrokenTrailer}
                            navigation={{
                              mode: "actions",
                              movie: currentMovie,
                              onLike: handleLike,
                              onDislike: handleDislike,
                              isLiked: isCurrentMovieSwiped,
                              isSwiping: swiping,
                              onRevert: handleRevert,
                              canRevert: swipeHistory.length > 0,
                            }}
                          />
                        </div>
                      </div>

                      <div className={styles.playerThumbs}>
                        <RoomMovieCards
                          movies={movies}
                          activeIndex={currentIndex}
                          onMovieClick={setCurrentIndex}
                          onSlideChange={setCurrentIndex}
                          syncedHeight={videoHeight}
                          onSwiper={(swiper) => {
                            swiperRef.current = swiper;
                          }}
                        />
                      </div>
                    </div>

                    <RoomMovieInfo
                      movie={currentMovie}
                      onLike={handleLike}
                      onDislike={handleDislike}
                      onShare={() => setInviteModalOpen(true)}
                      isLiked={isCurrentMovieSwiped}
                      isSwiping={swiping}
                      favouritesHandler={favouritesHandler}
                      isFavorited={(favoritesList?.data?.results || []).includes(currentMovie.id)}
                      openLoginModal={openLoginModal}
                      likesList={likesList?.data?.results || []}
                      likeHandler={likeHandler}
                      updateVoteLocal={updateMovieCountLocal}
                    />

                    {isMobile && (
                      <div
                        className={`${styles.roomSelectedButtons} ${matches.length > 0 ? styles.hasMatches : ""}`}
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                      >
                        {t("room_matches", "Совпадения")}
                        <span>
                          {matches.length}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className={styles.emptyState}>
                    {t("room_no_more_movies", "No more movies")}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {phase === "results" && room && (
          room.status === "completed" ? (
            <div className={styles.resultsComplete}>
              <div className={styles.resultsSelected}>
                <div className={styles.resultsSelectedHolder}>
                  <div className={styles.resultsSelectedHeading}>
                    {t("room_completed", "Movie Selected!")}
                  </div>

                  {selectedMatch && (
                    <div className={styles.resultCard}>
                      {selectedMatch.movie_poster_path && (
                        <div className={styles.resultCardPoster}>
                          <img
                            src={`${process.env.REACT_APP_IMG_BASE_URL}${selectedMatch.movie_poster_path}`}
                            alt={movieTitleHandler(i18n.language, selectedMatch.movie_detail || selectedMatch) || selectedMatch.movie_title}
                          />
                        </div>
                      )}

                      <div className={styles.resultCardHolder}>
                        <div className={styles.resultCardHeading}>
                          <div className={styles.resultCardTitle}>
                            {movieTitleHandler(i18n.language, selectedMatch.movie_detail || selectedMatch) || selectedMatch.movie_title}
                          </div>
                          <div
                            className={styles.savedIconWrapper}
                            onClick={(e) => {
                              e.stopPropagation();
                              const token = localStorage.getItem("accessToken");
                              if (!token) return openLoginModal();
                              favouritesHandler(e, { id: selectedMatch.movie_id }, selectedMatchFavorited);
                            }}
                          >
                            <SavedIcon className={styles.savedIconDesctop} inFavourites={selectedMatchFavorited} />
                          </div>
                        </div>

                        {(selectedMeta.year || selectedMeta.genreList.length > 0) && (
                          <div className={styles.meta}>
                            <div className={styles.metaHolder}>
                              {selectedMeta.year && <span className={styles.year}>{selectedMeta.year}</span>}
                            </div>

                            {selectedMeta.genreList.length > 0 && (
                              <div className={styles.genres}>
                                {selectedMeta.genreList.join(", ")}
                              </div>
                            )}
                          </div>
                        )}

                        <div className={styles.resultCardActions} >
                          {(selectedImdbRating || selectedTomatoRating) && (
                            <>
                              {selectedImdbRating && (
                                <div className={styles.resultCardRatingItem}>
                                  <OverlayRatingIcon />
                                  <p className={styles.rating}>
                                    {selectedImdbRating.toFixed(1)}
                                  </p>
                                </div>
                              )}
                              {selectedTomatoRating && (
                                <div className={styles.resultCardRatingItem}>
                                  <OverlayTomatoRatingIcon />
                                  <p className={styles.rating}>
                                    {selectedTomatoRating.toFixed(0)}
                                  </p>
                                </div>
                              )}
                            </>
                          )}

                          <LikeButton
                            clickHandler={(e: any, item: any, liked: boolean) => {
                              const token = localStorage.getItem("accessToken");
                              if (!token) return openLoginModal();
                              likeHandler(e, item, liked);
                            }}
                            direction="row"
                            isMobile={false}
                            count={formatLikesCount(selectedLikesCount)}
                            updateFieldLocal={updateVoteCount}
                            liked={selectedMatchLiked}
                            item={{ id: selectedMatch.movie_id }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className={styles.resultsButtons}>
                  <button className={styles.newRoomBtn} onClick={handleNewRoom}>
                    {t("room_new_room", "Create New Room")}
                  </button>
                  <button className={styles.closeRoomBtn} onClick={handleGoHome}>
                    {t("room_end_session", "Close Room")}
                  </button>
                </div>
              </div>

              <div className={styles.resultMatches}>
                {matches.length > 0 && (
                  <div className={styles.allMatches}>
                    <div className={styles.allMatchesTitle}>
                      {t("room_all_matches", "All Matches")}
                    </div>

                    <div className={styles.matchItems}>
                      {matches.map((m) => {
                        const detail = m.movie_detail ?? null;
                        const titleCard = movieTitleHandler(i18n.language, m.movie_detail || m) || m.movie_title;
                        const imdbRating = detail?.imdb_rating ?? detail?.vote_average ?? null;
                        const tomatoRating = detail?.rotten_tomatoes_rating ?? null;
                        const likesCount = detail?.likes_count ?? 0;
                        const isLiked = (likesList?.data?.data || []).includes(m.movie_id);
                        const isFavorited = (favoritesList?.data?.results || []).includes(m.movie_id);
                        const meta = getMovieMeta(detail);

                        return (
                          <div
                            key={m.id}
                            className={`${styles.matchItem} ${matches.length === 1 ? styles.single : ""}`}
                          >
                            {m.movie_poster_path && (
                              <div className={styles.matchItemPoster}>
                                <img
                                  src={`${process.env.REACT_APP_IMG_BASE_URL}${m.movie_poster_path}`}
                                  alt={titleCard}
                                />
                              </div>
                            )}

                            <div className={styles.matchItemDescription}>
                              <div className={styles.matchItemHeading}>
                          <span className={styles.matchItemTitle}>
                            {titleCard}
                          </span>
                              </div>

                              {(meta.year || meta.genreList.length > 0) && (
                                <div className={styles.meta}>
                                  <div className={styles.metaHolder}>
                                    {meta.year && <span className={`${styles.year} ${styles.matchItemYear}`}>{meta.year}</span>}
                                  </div>

                                  {meta.genreList.length > 0 && (
                                    <div className={styles.genres}>
                                      {meta.genreList.join(", ")}
                                    </div>
                                  )}
                                </div>
                              )}

                              <div className={styles.resultCardActions} >
                                {(imdbRating || tomatoRating) && (
                                  <>
                                    {imdbRating && (
                                      <div className={styles.resultCardRatingItem}>
                                        <OverlayRatingIcon />
                                        <p className={styles.rating}>{imdbRating.toFixed(1)}</p>
                                      </div>
                                    )}
                                    {tomatoRating && (
                                      <div className={styles.resultCardRatingItem}>
                                        <OverlayTomatoRatingIcon />
                                        <p className={styles.rating}>{tomatoRating.toFixed(0)}</p>
                                      </div>
                                    )}
                                  </>
                                )}

                                <LikeButton
                                  clickHandler={(e: any, item: any, liked: boolean) => {
                                    const token = localStorage.getItem("accessToken");
                                    if (!token) return openLoginModal();
                                    likeHandler(e, item, liked);
                                  }}
                                  direction="row"
                                  isMobile={false}
                                  count={formatLikesCount(likesCount)}
                                  updateFieldLocal={updateVoteCount}
                                  liked={isLiked}
                                  item={{ id: m.movie_id }}
                                />
                              </div>
                            </div>

                            <div className={styles.matchItemSavedButton}>
                              <div
                                className={styles.savedIconWrapper}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const token = localStorage.getItem("accessToken");
                                  if (!token) return openLoginModal();
                                  favouritesHandler(e, { id: m.movie_id }, isFavorited);
                                }}
                              >
                                <SavedIcon className={styles.savedIconDesctop} inFavourites={isFavorited} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

          ) : (
            <div className={styles.results}>
              <div className={styles.resultsHeading}>
                <div className={styles.resultsTitle}>
                  {room.status === "expired" ? t("room_expired", "Room Expired") : t("room_cancelled", "Room Cancelled")}
                </div>
                <span className={styles.resultsSubTitle}>{t("room_new_room_subtitle", "Create a new room")}</span>
              </div>

              <button className={styles.newRoomBtn} onClick={handleNewRoom}>
                {t("room_new_room", "Create New Room")}
              </button>
            </div>
          )
        )}

      </div>
      <Footer source="" />
      {phase === "browse" && room && (
        <>
          <RoomInviteModal
            roomCode={room.code}
            isOpen={inviteModalOpen}
            onClose={() => setInviteModalOpen(false)}
            onCopy={() => {
              if (currentMovie) {
                updateMovieCountLocal(currentMovie.id, "shares_count", "increment");
              }
            }}
          />
          <RoomGenreFilter
            selectedGenreIds={selectedGenreIds}
            onToggleGenre={toggleGenre}
            onApply={() => setGenreFilterOpen(false)}
            onClose={() => setGenreFilterOpen(false)}
            isOpen={genreFilterOpen}
          />
        </>

      )}
      {matchToast && (
        <div className={styles.matchToast}>
          {t("room_match_toast", "It's a match!")}
        </div>
      )}
      <LinkGuide/>
    </>
  );
};

export default RoomPage;
